import type { Order, Payment } from '~/types'
import { PAYMENT_METHOD_LABELS, PAYMENT_STATUS_LABELS } from '~/types'
import { parseNetopiaMobilpayIpn } from '~/utils/netopia-ipn'

export function getOrderPayment(order: Order): Payment | undefined {
  return order.payments?.[0] ?? order.payment
}

export function getPaymentMethodLabel(method?: string): string {
  if (!method) return '—'
  return PAYMENT_METHOD_LABELS[method] ?? method
}

export function getPaymentMethodColor(method?: string): 'primary' | 'info' | 'warning' | 'neutral' {
  switch (method) {
    case 'stripe':
      return 'neutral'
    case 'euplatesc':
    case 'payu':
    case 'netopia':
    case 'card':
      return 'info'
    case 'cash_on_delivery':
      return 'warning'
    case 'bank_transfer':
      return 'primary'
    case 'tbi':
      return 'info'
    default:
      return 'neutral'
  }
}

export function getPaymentMethodBadgeClass(method?: string): string | undefined {
  if (method === 'stripe') return 'payment-chip--stripe'
  if (method === 'tbi') return 'payment-chip--tbi'
  return undefined
}

export function getPaymentMethodIcon(method?: string): string {
  switch (method) {
    case 'netopia':
    case 'stripe':
    case 'euplatesc':
    case 'payu':
    case 'card':
      return 'i-heroicons-credit-card'
    case 'cash_on_delivery':
      return 'i-heroicons-banknotes'
    case 'bank_transfer':
      return 'i-heroicons-building-library'
    case 'tbi':
      return 'i-heroicons-calendar-days'
    case 'order_request':
      return 'i-heroicons-clipboard-document-list'
    default:
      return 'i-heroicons-question-mark-circle'
  }
}

export function getPaymentStatusLabel(status?: string): string {
  if (!status) return '—'
  return PAYMENT_STATUS_LABELS[status] ?? status
}

export function getPaymentStatusTextClass(status?: string): string {
  switch (status) {
    case 'completed':
      return 'text-green-700'
    case 'failed':
      return 'text-red-600'
    case 'refunded':
      return 'text-gray-600'
    case 'pending':
    default:
      return 'text-amber-700'
  }
}

export interface NetopiaIpnDetails {
  action?: string
  errorCode?: string
  errorMessage?: string
  processedAmount?: string
  originalAmount?: string
  purchaseId?: string
  rrn?: string
}

export function getNetopiaIpnDetails(response?: Record<string, unknown>): NetopiaIpnDetails | null {
  if (!response) return null

  const ipn = parseNetopiaMobilpayIpn(response)
  if (!ipn.action && !ipn.errorCode) return null

  return {
    action: ipn.action,
    errorCode: ipn.errorCode,
    errorMessage: ipn.errorMessage,
    processedAmount: ipn.processedAmount,
    originalAmount: ipn.originalAmount,
    purchaseId: ipn.transactionId,
    rrn: ipn.rrn,
  }
}

export function isNetopiaPayment(payment?: Payment): boolean {
  return payment?.method === 'netopia' || payment?.method === 'card'
}

export function isStripePayment(payment?: Payment): boolean {
  return payment?.method === 'stripe'
}

export function isEuplatescPayment(payment?: Payment): boolean {
  return payment?.method === 'euplatesc'
}

export function isPayuPayment(payment?: Payment): boolean {
  return payment?.method === 'payu'
}

export function isTbiPayment(payment?: Payment): boolean {
  return payment?.method === 'tbi'
}

export function isOnlineCardPayment(payment?: Payment): boolean {
  return isNetopiaPayment(payment) || isStripePayment(payment) || isEuplatescPayment(payment) || isPayuPayment(payment)
}

export function canRetryNetopiaPayment(order: Order): boolean {
  const payment = getOrderPayment(order)
  if (!payment || !isNetopiaPayment(payment)) return false
  if (order.status !== 'pending') return false
  return payment.status === 'pending' || payment.status === 'failed'
}

export function canRetryOnlinePayment(order: Order): boolean {
  const payment = getOrderPayment(order)
  if (!payment) return false
  if (!isOnlineCardPayment(payment) && !isTbiPayment(payment)) return false
  if (order.status !== 'pending') return false
  return payment.status === 'pending' || payment.status === 'failed'
}

/** Suma de ramburs pentru Colete Online — ca în pluginul WordPress. */
export function resolveOrderRepaymentAmount(order: Order): number {
  const payment = getOrderPayment(order)
  if (payment?.method === 'cash_on_delivery' || order.status === 'ramburs') {
    return Number(order.total ?? 0)
  }
  if (payment?.method === 'bank_transfer') {
    return 0
  }
  if (payment?.method === 'tbi' && payment.status === 'completed') {
    return 0
  }
  if (payment?.method === 'tbi') {
    return 0
  }
  if (payment && isOnlineCardPayment(payment) && payment.status === 'completed') {
    return 0
  }
  if (payment && isOnlineCardPayment(payment)) {
    return 0
  }
  return Number(order.total ?? 0)
}
